// Cache-first wrapper around the public API.
//
// - First visit: fetches from the network and stores the response in localStorage.
// - Later visits: serves instantly from the cache, refreshing quietly in the
//   background when the cache has gone stale — so returning visitors never see
//   the site waiting on the network.
// - No cache + network failure: rejects, so pages fall back to their own
//   loading/empty states (no fabricated data ever).

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:7000'}/api/v1/public`;

const CACHE_TTL = 10 * 60 * 1000; // 10 minutes
const CACHE_PREFIX = 'ch-cache:';

/**
 * Broadcast API reachability so UI (e.g. the offline banner) can react.
 * Fired on network-level failures and on any successful response.
 */
export function broadcastApiStatus(reachable) {
  try {
    window.dispatchEvent(new CustomEvent('ch:api-status', { detail: { reachable } }));
  } catch {
    /* ignore */
  }
}

/** Quick reachability probe used by the retry button (bypasses the cache). */
export async function pingApi() {
  const res = await fetch(`${API_BASE}/company-info?_=${Date.now()}`);
  if (!res.ok) throw new Error(`API probe failed: ${res.status}`);
  await res.json();
  return true;
}

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || !entry.savedAt || !entry.data) return null;
    return entry;
  } catch {
    return null;
  }
};

const writeCache = (key, data) => {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ savedAt: Date.now(), data }));
  } catch {
    // Storage unavailable (private mode / quota) — cache is best-effort.
  }
};

/**
 * GET a public endpoint, returning the parsed JSON body ({ success, data }).
 * Resolves instantly from cache when available; revalidates in the background
 * when the cache is stale; rejects only when there is no cache and the
 * network fails.
 * Pass ttl: 0 to force a network-first fetch (still writes the cache); the
 * promise resolves with the FRESH response once it arrives.
 */
export async function apiGet(path, { ttl = CACHE_TTL } = {}) {
  const cacheKey = path.split('?')[0];
  const cached = ttl === 0 ? null : readCache(cacheKey);
  const isFresh = cached && Date.now() - cached.savedAt < ttl;

  if (isFresh) return cached.data;

  const fetchPromise = fetch(`${API_BASE}${path}`)
    .then((res) => {
      broadcastApiStatus(true);
      return res.json();
    })
    .then((json) => {
      if (json && json.success) {
        writeCache(cacheKey, json);
        return json;
      }
      throw new Error(`API error for ${path}`);
    })
    .catch((err) => {
      // Network-level failure (server unreachable, DNS, CORS refusal). An HTTP
      // error status means the server answered, so it doesn't set this off.
      if (err instanceof TypeError) broadcastApiStatus(false);
      throw err;
    });

  if (cached) {
    // Stale-but-present: serve it now, let the refresh finish in the background.
    fetchPromise.catch(() => {});
    return cached.data;
  }

  return fetchPromise;
}

/** All public endpoints the site needs on first paint, warmed into the cache. */
export function prefetchPublicData() {
  const paths = [
    '/company-info',
    '/services',
    '/faqs',
    '/testimonials',
    '/webinars',
    '/press-releases',
    '/statistics?activeOnly=true',
    '/fixed-income?activeOnly=true',
    '/leadership',
    '/awards?activeOnly=true',
  ];
  return Promise.allSettled(paths.map((p) => apiGet(p).catch(() => null)));
}

/** True when the core homepage data is already cached from a previous visit. */
export function hasWarmCache() {
  const core = ['/company-info', '/services'];
  return core.some((key) => {
    const cached = readCache(key);
    return cached && Date.now() - cached.savedAt < CACHE_TTL;
  });
}

export { API_BASE };